'use client';

import * as React from 'react';
import { Share2, Facebook, Twitter, Linkedin, Copy, Instagram } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { cn } from '@ui/lib/utils';
import { toast } from 'sonner';

interface ShareModalProps {
  url: string;
  title: string;
  children?: React.ReactNode;
}

export function ShareModal({ url, title, children }: ShareModalProps) {
  const encodedUrl = React.useMemo(() => encodeURI(url), [url]);

  const shareOptions = [
    {
      name: 'Instagram',
      icon: Instagram,
      color: 'bg-[#E8F1FE]',
      iconColor: '#E4405F',
      shareUrl: `https://www.instagram.com/`,
    },
    {
      name: 'X',
      icon: Twitter,
      color: 'bg-[#E8F1FE]',
      iconColor: '#000000',
      shareUrl: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        encodedUrl,
      )}&text=${encodeURIComponent(title)}`,
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-[#E8F1FE]',
      iconColor: '#1877F2',
      shareUrl: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(encodedUrl)}`,
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-[#E8F1FE]',
      iconColor: '#0077B5',
      shareUrl: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        encodedUrl,
      )}`,
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(encodedUrl);
      toast('Link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy text');
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild={children ? false : true} className="w-full">
        {children || (
          <Button
            variant="default"
            size="icon"
            className={cn(
              'bg-white rounded-full shadow-md h-10 w-10 lg:h-12 lg:w-12 text-black hover:bg-primaryHover',
            )}>
            <Share2 className={cn('h-4 w-4 lg:h-6 lg:w-6')} />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-normal">Share</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-6 py-8">
          {shareOptions.map((option) => (
            <a
              key={option.name}
              href={option.shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2">
              <div className={cn('rounded-full p-6', option.color)}>
                <option.icon className="h-6 w-6" style={{ color: option.iconColor }} />
              </div>
              <span className="text-sm">{option.name}</span>
            </a>
          ))}
        </div>
        <div className="">
          <div className="flex items-center gap-2 bg-[#F8F9FD] p-4 rounded-lg">
            <input
              type="text"
              value={encodedUrl}
              readOnly
              className="flex-1 bg-transparent outline-none text-gray-600"
            />
            <button
              onClick={copyToClipboard}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Copy className="h-5 w-5" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
